import type { Product, Category, PricingModel } from "./types"

export const products: Product[] = [
  {
    id: "1",
    name: "BikeBox Smart Parking",
    slug: "bikebox-smart-parking",
    description:
      "A comprehensive smart parking solution for bicycles that integrates with city infrastructure. BikeBox Smart Parking provides secure, IoT-enabled parking spots with real-time availability tracking and mobile access.",
    shortDescription: "Secure, IoT-enabled bicycle parking solution",
    manufacturer: {
      name: "BikeBox GmbH",
      logo: "/bikebox-logo.png",
    },
    category: "Smart Infrastructure",
    subcategory: "Parking Solutions",
    image: "/smart-bicycle-parking.png",
    features: [
      "Real-time availability tracking",
      "Mobile access control",
      "Anti-theft protection",
      "Usage analytics",
      "Maintenance alerts",
    ],
    pricing: {
      model: "Monthly Subscription",
      startingAt: 299,
      currency: "EUR",
    },
    rating: 4.8,
    reviewCount: 124,
    integrations: ["JUHUU Map", "JUHUU App", "JUHUU Dashboard"],
    tags: ["parking", "bicycles", "smart city", "IoT"],
  },
  {
    id: "2",
    name: "UrbanMobility Fleet Manager",
    slug: "urbanmobility-fleet-manager",
    description:
      "A comprehensive fleet management solution for bike and scooter sharing operators. Track, manage, and optimize your entire fleet with real-time analytics, maintenance scheduling, and user behavior insights.",
    shortDescription: "Complete fleet management for bike and scooter sharing",
    manufacturer: {
      name: "UrbanMobility Solutions",
      logo: "/urbanmobility-logo.png",
    },
    category: "Mobility Solutions",
    subcategory: "Fleet Management",
    image: "/bike-fleet-management.png",
    features: [
      "Real-time fleet tracking",
      "Predictive maintenance",
      "Battery management",
      "User behavior analytics",
      "Rebalancing optimization",
    ],
    pricing: {
      model: "Tiered Pricing",
      startingAt: 499,
      currency: "EUR",
    },
    rating: 4.6,
    reviewCount: 87,
    integrations: ["JUHUU Map", "JUHUU Dashboard", "JUHUU Digital Twins"],
    tags: ["fleet management", "bike sharing", "scooter sharing", "mobility"],
  },
  {
    id: "3",
    name: "CityTwin Infrastructure Monitoring",
    slug: "citytwin-infrastructure-monitoring",
    description:
      "Create digital twins of your urban infrastructure to monitor, simulate, and optimize city operations. CityTwin provides real-time insights into traffic patterns, energy usage, and infrastructure health.",
    shortDescription: "Digital twin solution for urban infrastructure",
    manufacturer: {
      name: "CityTwin Technologies",
      logo: "/citytwin-logo.png",
    },
    category: "Digital Twins",
    subcategory: "Urban Infrastructure",
    image: "/placeholder.svg?height=300&width=400&query=Digital%20Twin%20City",
    features: [
      "Real-time infrastructure monitoring",
      "Predictive maintenance",
      "Scenario simulation",
      "Energy optimization",
      "Traffic flow analysis",
    ],
    pricing: {
      model: "Annual Subscription",
      startingAt: 9999,
      currency: "EUR",
    },
    rating: 4.9,
    reviewCount: 42,
    integrations: ["JUHUU Map", "JUHUU Dashboard", "JUHUU Data Service"],
    tags: ["digital twins", "smart city", "infrastructure", "simulation"],
  },
  {
    id: "4",
    name: "MobilityInsights Analytics Platform",
    slug: "mobilityinsights-analytics",
    description:
      "Advanced analytics platform for mobility data. Transform raw data into actionable insights with AI-powered analytics, custom dashboards, and automated reporting for mobility service providers.",
    shortDescription: "AI-powered analytics for mobility data",
    manufacturer: {
      name: "MobilityInsights",
      logo: "/mobilityinsights-logo.png",
    },
    category: "Data Analytics",
    subcategory: "Mobility Analytics",
    image: "/placeholder.svg?height=300&width=400&query=Mobility%20Data%20Analytics",
    features: [
      "AI-powered data analysis",
      "Custom dashboards",
      "Automated reporting",
      "Trend prediction",
      "User behavior insights",
    ],
    pricing: {
      model: "Tiered Pricing",
      startingAt: 799,
      currency: "EUR",
    },
    rating: 4.7,
    reviewCount: 63,
    integrations: ["JUHUU Dashboard", "JUHUU Data Service", "JUHUU Digital Twins"],
    tags: ["analytics", "mobility data", "AI", "business intelligence"],
  },
  {
    id: "5",
    name: "AccessHub Identity Management",
    slug: "accesshub-identity-management",
    description:
      "Secure identity and access management solution for mobility services. AccessHub provides seamless authentication, authorization, and user management across multiple platforms and services.",
    shortDescription: "Identity management for mobility services",
    manufacturer: {
      name: "AccessHub Security",
      logo: "/accesshub-logo.png",
    },
    category: "User Access",
    subcategory: "Identity Management",
    image: "/placeholder.svg?height=300&width=400&query=Identity%20Management",
    features: [
      "Single sign-on (SSO)",
      "Multi-factor authentication",
      "Role-based access control",
      "User lifecycle management",
      "Compliance reporting",
    ],
    pricing: {
      model: "Monthly Subscription",
      startingAt: 399,
      currency: "EUR",
    },
    rating: 4.5,
    reviewCount: 91,
    integrations: ["JUHUU App", "JUHUU WebApp", "JUHUU Identity"],
    tags: ["identity", "security", "access management", "authentication"],
  },
  {
    id: "6",
    name: "BookNow Reservation System",
    slug: "booknow-reservation",
    description:
      "Comprehensive booking and reservation system for mobility services. BookNow enables seamless scheduling, resource allocation, and payment processing for bike sharing, parking, and other mobility services.",
    shortDescription: "Advanced booking system for mobility services",
    manufacturer: {
      name: "BookNow Systems",
      logo: "/booknow-logo.png",
    },
    category: "Booking Systems",
    subcategory: "Reservation Management",
    image: "/placeholder.svg?height=300&width=400&query=Booking%20System",
    features: [
      "Real-time availability",
      "Automated scheduling",
      "Resource optimization",
      "Payment processing",
      "Booking analytics",
    ],
    pricing: {
      model: "Pay-per-use",
      startingAt: 0.1,
      currency: "EUR",
    },
    rating: 4.6,
    reviewCount: 108,
    integrations: ["JUHUU App", "JUHUU WebApp", "JUHUU Billing"],
    tags: ["booking", "reservations", "scheduling", "mobility services"],
  },
  {
    id: "7",
    name: "PayMobility Payment Gateway",
    slug: "paymobility-gateway",
    description:
      "Specialized payment processing solution for mobility services. PayMobility handles subscriptions, usage-based billing, and multi-currency transactions with seamless integration into mobility platforms.",
    shortDescription: "Payment gateway optimized for mobility services",
    manufacturer: {
      name: "PayMobility Fintech",
      logo: "/paymobility-logo.png",
    },
    category: "Payment Solutions",
    subcategory: "Payment Gateway",
    image: "/placeholder.svg?height=300&width=400&query=Payment%20Gateway",
    features: [
      "Subscription management",
      "Usage-based billing",
      "Multi-currency support",
      "Fraud detection",
      "Revenue analytics",
    ],
    pricing: {
      model: "Pay-per-use",
      startingAt: 0.015,
      currency: "EUR",
    },
    rating: 4.7,
    reviewCount: 76,
    integrations: ["JUHUU Billing", "JUHUU App", "JUHUU WebApp"],
    tags: ["payments", "billing", "subscriptions", "fintech"],
  },
  {
    id: "8",
    name: "ConnectMobility Integration Platform",
    slug: "connectmobility-integration",
    description:
      "API integration platform for mobility services. ConnectMobility enables seamless data exchange and service integration between different mobility providers, city infrastructure, and third-party applications.",
    shortDescription: "API platform for mobility service integration",
    manufacturer: {
      name: "ConnectMobility",
      logo: "/connectmobility-logo.png",
    },
    category: "Integration Tools",
    subcategory: "API Platform",
    image: "/placeholder.svg?height=300&width=400&query=API%20Integration%20Platform",
    features: [
      "API management",
      "Data transformation",
      "Service orchestration",
      "Monitoring and analytics",
      "Developer portal",
    ],
    pricing: {
      model: "Tiered Pricing",
      startingAt: 599,
      currency: "EUR",
    },
    rating: 4.8,
    reviewCount: 52,
    integrations: ["JUHUU API", "JUHUU Data Service", "JUHUU Digital Twins"],
    tags: ["API", "integration", "interoperability", "mobility services"],
  },
  {
    id: "9",
    name: "SmartRoute AI Routing Engine",
    slug: "smartroute-ai",
    description:
      "AI-powered routing and navigation solution for mobility services. SmartRoute optimizes routes based on real-time traffic, weather, and infrastructure data to improve efficiency and user experience.",
    shortDescription: "AI routing optimization for mobility services",
    manufacturer: {
      name: "SmartRoute AI",
      logo: "/smartroute-logo.png",
    },
    category: "Mobility Solutions",
    subcategory: "Routing & Navigation",
    image: "/placeholder.svg?height=300&width=400&query=AI%20Routing",
    features: [
      "Real-time route optimization",
      "Multi-modal routing",
      "Traffic prediction",
      "Battery-aware routing for EVs",
      "Accessibility options",
    ],
    pricing: {
      model: "Monthly Subscription",
      startingAt: 699,
      currency: "EUR",
    },
    rating: 4.9,
    reviewCount: 38,
    integrations: ["JUHUU Map", "JUHUU App", "JUHUU AI Routing"],
    tags: ["routing", "navigation", "AI", "mobility"],
  },
  {
    id: "10",
    name: "BikeTrack Asset Management",
    slug: "biketrack-asset-management",
    description:
      "IoT-based asset tracking and management solution for bike fleets. BikeTrack provides real-time location tracking, theft prevention, and maintenance management for bike sharing operators.",
    shortDescription: "IoT asset tracking for bike fleets",
    manufacturer: {
      name: "BikeTrack Technologies",
      logo: "/biketrack-logo.png",
    },
    category: "Mobility Solutions",
    subcategory: "Asset Tracking",
    image: "/placeholder.svg?height=300&width=400&query=Bike%20Asset%20Tracking",
    features: [
      "Real-time GPS tracking",
      "Geofencing",
      "Theft prevention",
      "Maintenance scheduling",
      "Battery monitoring",
    ],
    pricing: {
      model: "Tiered Pricing",
      startingAt: 349,
      currency: "EUR",
    },
    rating: 4.6,
    reviewCount: 83,
    integrations: ["JUHUU Map", "JUHUU Dashboard", "JUHUU Digital Twins"],
    tags: ["asset tracking", "IoT", "bike sharing", "fleet management"],
  },
]

export const categories: Category[] = [
  "Mobility Solutions",
  "Smart Infrastructure",
  "Digital Twins",
  "Data Analytics",
  "User Access",
  "Booking Systems",
  "Payment Solutions",
  "Integration Tools",
]

export const pricingModels: PricingModel[] = [
  "Monthly Subscription",
  "Annual Subscription",
  "Pay-per-use",
  "Tiered Pricing",
  "Custom Pricing",
]
